"use client";

import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { PageHero } from "@/components/content/page-hero";
import { ContentContainer } from "@/components/content";
import { Button } from "@/components/ui/button";
import { BarChart, Database, FileUp, Github, Plus, Upload } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";

export default function HomePage() {

  return (
    <div className="h-full w-full flex flex-col">
      <div className="flex-1 overflow-auto">
        <PageHero 
          title="Welcome to TabLab"
          description="Your data analysis and visualization platform"
          variant="tablab"
          noBorder={true}
        >
          <ContentContainer className="pt-2 pb-8">
            {/* Quick Actions Section */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {/* Open File Card */}
              <Card className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 hover:from-blue-500/20 hover:to-cyan-500/20 transition-colors cursor-pointer group">
                <Link href="/app" className="no-underline">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileUp className="h-5 w-5 text-blue-500" />
                      Open a File
                    </CardTitle>
                    <CardDescription>
                      Upload and analyze your data files instantly
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      Drag and drop or select CSV, JSON, Parquet files to start analyzing
                    </p>
                  </CardContent>
                </Link>
              </Card>

              {/* Create Dataset Card */}
              <Card className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 hover:from-purple-500/20 hover:to-pink-500/20 transition-colors cursor-pointer group">
                <Link href="/datasets" className="no-underline">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Plus className="h-5 w-5 text-purple-500" />
                      Create Dataset
                    </CardTitle>
                    <CardDescription>
                      Create a new dataset from your files
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      Import and combine multiple data sources into a single dataset
                    </p>
                  </CardContent>
                </Link>
              </Card>

              {/* Browse GitHub Card */}
              <Card className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 hover:from-green-500/20 hover:to-emerald-500/20 transition-colors cursor-pointer group">
                <Link href="/datasets/github" className="no-underline">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Github className="h-5 w-5 text-green-500" />
                      Browse GitHub
                    </CardTitle>
                    <CardDescription>
                      Access datasets from GitHub repositories
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      View and analyze CSV files from any public GitHub repository
                    </p>
                  </CardContent>
                </Link>
              </Card>
            </div>

            {/* Quick Features Section */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4">Quick Features</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <BarChart className="h-4 w-4 text-blue-500" />
                      Data Visualization
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Create charts and graphs to explore patterns in your data</p>
                  </CardContent>
                  <CardFooter>
                    <Button variant="secondary" asChild className="w-full">
                      <Link href="/graphs" className="no-underline">Explore Visualizations</Link>
                    </Button>
                  </CardFooter>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Database className="h-4 w-4 text-purple-500" />
                      Data Analysis
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Filter, sort, and transform data to extract valuable insights</p>
                  </CardContent>
                  <CardFooter>
                    <Button variant="secondary" asChild className="w-full">
                      <Link href="/statistics" className="no-underline">View Analysis Tools</Link>
                    </Button>
                  </CardFooter>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Upload className="h-4 w-4 text-green-500" />
                      File Conversion
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <p>Convert between CSV, JSON, Parquet and other formats</p>
                  </CardContent>
                  <CardFooter>
                    <Button variant="secondary" asChild className="w-full">
                      <Link href="/csv/to" className="no-underline">Convert Files</Link>
                    </Button>
                  </CardFooter>
                </Card>
              </div>
            </div>
          </ContentContainer>
        </PageHero>
      </div>
    </div>
  );
} 